import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { BiStore } from 'react-icons/bi';
import { FaShippingFast } from 'react-icons/fa';
import { FiCheckCircle, FiClock, FiTruck, FiAlertCircle } from 'react-icons/fi';

import Actions from '../../constants/Actions';
import { getCepUser, getDataUsuario } from '../../services/auth';
import buscaCep from '../../actions/buscaCep';
import cotarCorreios from '../../actions/global/cotarCorreios';
import buscarFilialPorCodigoFilial from '../../actions/cadastro/buscar-filial-por-codigo-filial';
import actionConfigVendaFacil from '../../actions/config/actionConfigVendaFacil';
import actionBuscarFiliaisCidades from '../../actions/config/actionBuscarFiliaisCidades';
import {
  getValorSubTotal,
  pesoCalculado,
  maiorLarguraItems,
  maiorComprimentoItems,
  alturaCalculado,
} from '../../utils/functions';

const BRAND = '#1a1a2e';
const BRAND_HOVER = '#16213e';
const BRAND_LIGHT = '#e8eaf0';
const ACCENT = '#0ea5e9';

const SHIPPING_PAC_ID = '61532d990d253dd8a450bcde';
const SHIPPING_PROPRIA_ID = '61560325eaf72f4fe7f5ee27';

const isCepLocal = (cep) => {
  const num = Number(cep);
  return num >= 35500000 && num <= 35505000;
};

const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DeliveryTypeCard = ({ icon, title, description, selected, disabled, onClick }) => (
  <Paper
    elevation={0}
    onClick={disabled ? undefined : onClick}
    sx={{
      flex: 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      p: 1.5,
      borderRadius: 1.5,
      border: '2px solid',
      borderColor: selected ? BRAND : 'grey.300',
      bgcolor: selected ? BRAND_LIGHT : '#fff',
      transition: 'all 0.15s ease',
      position: 'relative',
      '&:hover': { borderColor: disabled ? 'grey.300' : BRAND, bgcolor: disabled ? '#fff' : BRAND_LIGHT },
    }}
  >
    {selected && (
      <Box sx={{ position: 'absolute', top: 6, right: 6, color: BRAND, display: 'flex' }}>
        <FiCheckCircle size={16} />
      </Box>
    )}
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: selected ? BRAND : BRAND_LIGHT,
          color: selected ? '#fff' : BRAND,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: selected ? BRAND : 'text.primary' }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const ShippingOptionCard = ({ selected, title, price, eta, onClick, free }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      p: 1.5,
      borderRadius: 1.5,
      border: '2px solid',
      borderColor: selected ? BRAND : 'grey.300',
      bgcolor: selected ? BRAND_LIGHT : '#fff',
      transition: 'all 0.15s ease',
      '&:hover': { borderColor: BRAND, bgcolor: BRAND_LIGHT },
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: selected ? BRAND : BRAND_LIGHT,
            color: selected ? '#fff' : BRAND,
            flexShrink: 0,
          }}
        >
          <FiTruck size={16} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{title}</Typography>
          {eta && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
              <FiClock size={11} />
              <Typography sx={{ fontSize: '0.7rem' }}>{eta}</Typography>
            </Stack>
          )}
        </Box>
      </Stack>
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: free ? '#16a34a' : BRAND, flexShrink: 0 }}>
        {free ? 'GRÁTIS' : `R$ ${formatBRL(price)}`}
      </Typography>
    </Stack>
  </Paper>
);

const CheckoutShipping = () => {
  const dispatch = useDispatch();

  const cepOrigemProduto = useSelector((state) => state.cepOrigemProduto);
  const cartItems = useSelector((state) => state.cartItems);
  const filialUsuario = getDataUsuario().filial;

  const [tipoEntrega, setTipoEntrega] = useState('Retirada');
  const [transportadora, setTransportadora] = useState(null);
  const [cepProduto, setCepProduto] = useState('');
  const [freteCorreios, setFreteCorreios] = useState('');
  const [mostrarFretesEntregas, setMostrarFretesEntregas] = useState(false);
  const [isLoadingFrete, setIsLoadingFrete] = useState(false);
  const [cepDigitado, setCepDigitado] = useState('');
  const [valorFreteRetirada, setValorFreteRetirada] = useState(0);
  const [entregaBloqueada, setEntregaBloqueada] = useState(false);
  const [retiradaGratis, setRetiradaGratis] = useState(false);
  const [cidade, setCidade] = useState('');
  const [erroFrete, setErroFrete] = useState('');

  const date = new Date();
  const subtotal = getValorSubTotal(cartItems);

  async function buscarConfig() {
    try {
      const res = await actionConfigVendaFacil();
      return res.data.data.vendafacil;
    } catch (err) {
      return null;
    }
  }

  async function validaEntregaFranquia() {
    const res_filiais_cidades = await actionBuscarFiliaisCidades(filialUsuario);
    if (!res_filiais_cidades.data.data) return;
    const res_config = await buscarConfig();
    if (!res_config) return;
    const filiaisCidades = res_filiais_cidades.data.data.filiais;
    const isFilialImpedida = res_config.filais_impedidas_entrega.includes(filialUsuario);

    if (!isFilialImpedida) return;
    if (!cartItems || cartItems.length === 0) return;

    const filiaisCarrinho = cartItems.map((i) => i.filial);
    if (filiaisCidades.some((valor) => filiaisCarrinho.includes(valor))) {
      setEntregaBloqueada(true);
      setRetiradaGratis(true);
      setValorFreteRetirada(0);
    }
  }

  useEffect(() => {
    // Limpa qualquer escolha anterior de frete para forçar nova seleção
    dispatch({ type: Actions.SHIPPING_ID, shippingId: null });
    dispatch({ type: Actions.TIPO_ENTREGA, tipoEntrega: 'Retirada' });
    dispatch({ type: Actions.valorFrete, valorFrete: 0 });

    if (!cartItems || cartItems.length === 0) return;
    validaEntregaFranquia();
    buscarFilialPorCodigoFilial(cepOrigemProduto).then((res) => {
      setCepProduto(res.data.cep);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuscaCep = async (zipCode) => {
    try {
      const address = await buscaCep(zipCode);
      if (address.erro) {
        setErroFrete('CEP não encontrado.');
        return null;
      }
      setCidade(address.localidade);
      return address;
    } catch {
      setErroFrete('CEP não encontrado.');
      return null;
    }
  };

  const calcularFreteFinal = (rawValor, ufDestino) => {
    if (subtotal > 199 && ufDestino === 'MG') return '0';
    if (Number(rawValor) < 1) return '20';
    return String(rawValor).replace(',', '.');
  };

  const handleBuscarFreteEntrega = async (e) => {
    e.preventDefault();
    setErroFrete('');
    setIsLoadingFrete(true);
    setMostrarFretesEntregas(true);

    const address = await handleBuscaCep(cepDigitado);
    if (!address) {
      setIsLoadingFrete(false);
      setMostrarFretesEntregas(false);
      return;
    }

    try {
      const res = await cotarCorreios({
        altura: alturaCalculado(cartItems),
        comprimento: maiorComprimentoItems(cartItems),
        largura: maiorLarguraItems(cartItems),
        peso: pesoCalculado(cartItems),
        cepOrigem: cepProduto,
        cepDestino: cepDigitado,
      });
      const sedexValor = res.data.result.sedex.Valor;
      setFreteCorreios(calcularFreteFinal(sedexValor, address.uf));
    } catch {
      setFreteCorreios('20');
      setErroFrete('Não foi possível cotar nos Correios. Aplicado valor padrão.');
    } finally {
      setIsLoadingFrete(false);
    }
  };

  const handleRetirada = () => {
    dispatch({ type: Actions.SHIPPING_ID, shippingId: null });
    setTipoEntrega('Retirada');
    setTransportadora(null);
    setMostrarFretesEntregas(false);
    dispatch({ type: Actions.TIPO_ENTREGA, tipoEntrega: 'Retirada' });

    if (retiradaGratis) {
      setValorFreteRetirada(0);
      return;
    }
    if (isCepLocal(getCepUser())) {
      setValorFreteRetirada(0);
    } else {
      setValorFreteRetirada(subtotal < 149 ? 20 : 0);
    }
  };

  const handleEntrega = () => {
    dispatch({ type: Actions.SHIPPING_ID, shippingId: null });
    setTipoEntrega('Entrega');
    setTransportadora(null);
    dispatch({ type: Actions.TIPO_ENTREGA, tipoEntrega: 'Entrega' });
  };

  const handleEscolhePac = () => {
    setTransportadora('Pac');
    dispatch({ type: Actions.valorFrete, valorFrete: Number.parseFloat(freteCorreios) });
    dispatch({ type: Actions.SHIPPING_ID, shippingId: SHIPPING_PAC_ID });
  };

  const validaEntregaPropria = () => {
    if (isCepLocal(cepDigitado)) {
      return subtotal < 199 ? 14.0 : 0.0;
    }
    return subtotal < 199 ? 18.0 : 0.0;
  };

  const handleEscolhePropria = () => {
    setTransportadora('Total');
    dispatch({ type: Actions.SHIPPING_ID, shippingId: SHIPPING_PROPRIA_ID });

    if (retiradaGratis) {
      dispatch({ type: Actions.valorFrete, valorFrete: 0.0 });
      return;
    }

    if (tipoEntrega === 'Retirada') {
      if (isCepLocal(getCepUser())) {
        dispatch({ type: Actions.valorFrete, valorFrete: 0.0 });
      } else {
        dispatch({ type: Actions.valorFrete, valorFrete: subtotal < 149 ? 20.0 : 0.0 });
      }
      return;
    }

    if (tipoEntrega === 'Entrega') {
      if (isCepLocal(cepDigitado)) {
        dispatch({ type: Actions.valorFrete, valorFrete: subtotal < 199 ? 14.0 : 0.0 });
      } else {
        dispatch({ type: Actions.valorFrete, valorFrete: subtotal < 199 ? 20.0 : 0.0 });
      }
    }
  };

  const valorRestanteRetirada = subtotal < 149 ? (150 - subtotal).toFixed(2) : null;
  const valorFretePropria = validaEntregaPropria();
  const dataMaximaPac = format(new Date(new Date().setDate(date.getDate() + 8)), 'dd/MM');

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        <DeliveryTypeCard
          icon={<BiStore size={20} />}
          title="Retirar na loja"
          description="Cliente busca na filial"
          selected={tipoEntrega === 'Retirada'}
          onClick={handleRetirada}
        />
        <DeliveryTypeCard
          icon={<FaShippingFast size={18} />}
          title="Entregar"
          description={entregaBloqueada ? 'Indisponível neste pedido' : 'Calcular frete'}
          selected={tipoEntrega === 'Entrega'}
          disabled={entregaBloqueada}
          onClick={handleEntrega}
        />
      </Stack>

      {tipoEntrega === 'Retirada' && (
        <Box sx={{ mt: 2 }}>
          <ShippingOptionCard
            selected={transportadora === 'Total'}
            title="Retirada na loja Katuxa"
            eta="Disponível após confirmação"
            price={valorFreteRetirada}
            free={valorFreteRetirada === 0}
            onClick={handleEscolhePropria}
          />
          {valorRestanteRetirada && valorFreteRetirada > 0 && (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{
                mt: 1.25,
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: '#fef3c7',
                border: '1px solid #fcd34d',
              }}
            >
              <FiAlertCircle size={16} style={{ color: '#d97706', marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.78rem', color: '#92400e' }}>
                Faltam <strong>R$ {valorRestanteRetirada}</strong> para retirada grátis.
              </Typography>
            </Stack>
          )}
        </Box>
      )}

      {tipoEntrega === 'Entrega' && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Informe o CEP de destino
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              placeholder="00000-000"
              value={cepDigitado}
              onChange={(e) => setCepDigitado(e.target.value.replace(/\D/g, ''))}
              inputProps={{ maxLength: 8, 'aria-label': 'CEP de destino' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '&.Mui-focused fieldset': { borderColor: BRAND },
                },
              }}
            />
            <Button
              onClick={handleBuscarFreteEntrega}
              disabled={cepDigitado.length !== 8 || isLoadingFrete}
              variant="contained"
              sx={{
                minWidth: 120,
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: BRAND,
                '&:hover': { bgcolor: BRAND_HOVER },
                '&:disabled': { bgcolor: '#d1d5db' },
              }}
            >
              Calcular
            </Button>
          </Stack>

          {erroFrete && (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ mt: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: '#fee2e2', border: '1px solid #fecaca' }}
            >
              <FiAlertCircle size={16} style={{ color: '#b91c1c', marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.78rem', color: '#991b1b' }}>{erroFrete}</Typography>
            </Stack>
          )}

          {mostrarFretesEntregas && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {isLoadingFrete ? (
                <>
                  <Skeleton variant="rounded" height={64} />
                  <Skeleton variant="rounded" height={64} />
                </>
              ) : (
                <>
                  {cidade !== 'Divinópolis' && freteCorreios !== '' && (
                    <ShippingOptionCard
                      selected={transportadora === 'Pac'}
                      title="Correios — PAC"
                      eta={`Chega até ${dataMaximaPac}`}
                      price={freteCorreios}
                      free={Number(freteCorreios) === 0}
                      onClick={handleEscolhePac}
                    />
                  )}
                  {cidade === 'Divinópolis' && (
                    <ShippingOptionCard
                      selected={transportadora === 'Total'}
                      title="Entrega Própria Katuxa"
                      eta="Em até 2 dias úteis após pagamento"
                      price={valorFretePropria}
                      free={valorFretePropria === 0}
                      onClick={handleEscolhePropria}
                    />
                  )}
                </>
              )}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CheckoutShipping;
